import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  Button,
  Tooltip,
  AccordionSummary,
  Accordion,
  AccordionDetails
} from '@mui/material';
import { Menu as MenuIcon, ArrowForwardIos as ArrowForwardIosIcon, Star as StarIcon } from '@mui/icons-material';
import React, { useEffect, useMemo, useState } from 'react';
import { styles } from './StoriesListSidebar.styles';
import { IAccordionListNode } from '@opengov/component-library/capital/AccordionList/AccordionList';
import { ArrowExpandLeft } from '@opengov/react-capital-assets';
import TextOverflowWithTooltip from '../TextOverflowWithTooltip/TextOverflowWithTooltip';
import { ISetupPdfMessages } from '../../../../../types/setupPDFTypes';
import { getStoriesListWithCoverStory } from '../SetupPdfUtils';
import { TOC_NODE_ID } from '@opengov/stories-shared/constants';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

type NodeToggleMap = { [nodeId: string]: boolean };

export interface IStoriesListSidebarProps {
  storiesSelectData: IAccordionListNode[] | null;
  openDrawer: boolean;
  messages: ISetupPdfMessages;
  setOpenDrawer: (open: boolean) => void;
  onStoryChange: (storyId: string) => void;
  coverStoryId: string | null;
  selectedStoryId: string | null;
  toggledStoriesVisibilityMap: NodeToggleMap;
  onToggledStoriesVisibilityMapChange: (map: NodeToggleMap) => void;
  storyMode: 'edit' | 'preview';
}

const renderAccordionNodes = ({
  nodes,
  level = 0,
  toggledSectionsMap,
  toggledStoriesVisibilityMap,
  messages,
  onNodeToggle,
  onStoryVisibilityToggle,
  selectedStoryId,
  onStoryChange,
  coverStoryId,
  storyMode,
  visibleStories
}: {
  nodes: IAccordionListNode[];
  level?: number;
  toggledSectionsMap: NodeToggleMap;
  toggledStoriesVisibilityMap: NodeToggleMap;
  messages: ISetupPdfMessages;
  onNodeToggle: (node: IAccordionListNode) => void;
  onStoryVisibilityToggle: (node: IAccordionListNode) => void;
  selectedStoryId: string | null;
  onStoryChange: (storyId: string) => void;
  coverStoryId: string | null;
  storyMode: 'edit' | 'preview';
  visibleStories: string[];
}) => {
  return nodes.map((node) => {
    if (node.children && node.children.length > 0) {
      return (
        <Accordion
          expanded={toggledSectionsMap[node.id] ?? false}
          onChange={() => onNodeToggle(node)}
          key={node.id}
          sx={styles.accordion}
        >
          <AccordionSummary
            expandIcon={<ArrowForwardIosIcon sx={styles.expandIcon} />}
            aria-controls={`${node.id}-content`}
            id={`${node.id}-header`}
          >
            <Typography variant="h4">{node.displayName}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={styles.accordionDetails}>
            {renderAccordionNodes({
              nodes: node.children,
              level: level + 1,
              toggledSectionsMap,
              toggledStoriesVisibilityMap,
              messages,
              onNodeToggle,
              onStoryVisibilityToggle,
              selectedStoryId,
              onStoryChange,
              coverStoryId,
              storyMode,
              visibleStories
            })}
          </AccordionDetails>
        </Accordion>
      );
    }

    const isSelectedStory = selectedStoryId === node.id;
    const isCoverStory = coverStoryId === node.id;

    return (

      <Box key={node.id} sx={{ ...styles.storyNode, ...(isSelectedStory ? styles.selectedStoryBgColor : {}), ...(toggledStoriesVisibilityMap[node.id] === false ? styles.storyDisabled : {}) }}>
        <Tooltip title={storyMode == 'preview' && toggledStoriesVisibilityMap[node.id] === false ? messages.hiddenStoryTooltip : ""} placement='bottom-start'>
          <Box sx={styles.storyTitleContainer}>
            <Button
              endIcon={isCoverStory && <StarIcon fontSize="small" sx={styles.storyStarIcon} />}
              sx={{
                ...styles.storyTitle,
                ...(isSelectedStory ? styles.selectedStory : {}),
                ...(node.id === TOC_NODE_ID ? styles.tocNodeTitle : {}),
                ...(toggledStoriesVisibilityMap[node.id] === false ? styles.storyDisabled : {})
              }}
              onClick={() => {
                if (toggledStoriesVisibilityMap[node.id] === false) {
                  return;
                }
                if (node.id !== selectedStoryId) {
                  onStoryChange(node.id);
                }
              }}
              disabled={toggledStoriesVisibilityMap[node.id] === false}
              aria-label={messages.goToStory({ storyName: node.displayName })}
            >

              <TextOverflowWithTooltip>{node.displayName}</TextOverflowWithTooltip>
            </Button>
          </Box>
        </Tooltip>
        {storyMode === 'edit' && node.id !== coverStoryId && node.id !== TOC_NODE_ID && (!node.children || node.children?.length == 0) ?
          <Tooltip title={visibleStories.length == 1 && node.id === visibleStories[visibleStories.length - 1] ? messages.visibleStoryTooltip : ""}>
            <Box>
              <IconButton disabled={(visibleStories.length == 1 && node.id === visibleStories[visibleStories.length - 1])} onClick={() => onStoryVisibilityToggle(node)} aria-label={messages.toggleStoryVisibility({ storyName: node.displayName })} >
                {toggledStoriesVisibilityMap[node.id] ? <VisibilityOutlinedIcon sx={styles.storyVisibilityIcon} /> : <VisibilityOffOutlinedIcon sx={styles.storyVisibilityIcon} />}
              </IconButton>
            </Box>
          </Tooltip>
          : <></>}

      </Box>
    );
  });
};

const StoriesListSidebarComponent = (props: IStoriesListSidebarProps) => {
  const {
    openDrawer,
    storiesSelectData: storiesList,
    setOpenDrawer,
    messages,
    onStoryChange,
    coverStoryId,
    selectedStoryId,
    toggledStoriesVisibilityMap,
    onToggledStoriesVisibilityMapChange,
    storyMode
  } = props;
  const [toggledSectionsMap, setToggledSectionsMap] = useState<NodeToggleMap>({});

  const updatedStoriesList = useMemo(() => {
    if (!storiesList) return [];
    return getStoriesListWithCoverStory(storiesList, coverStoryId);
  }, [storiesList, coverStoryId]);

  const { allSectionIds, allStoryIds, visibleStories } = useMemo(() => {
    if (!updatedStoriesList) return { allSectionIds: [], allStoryIds: [], visibleStories: [] };

    const allSectionIds: string[] = [];
    const allStoryIds: string[] = [];
    const visibleStories: string[] = [];
    const getIdsFromNodes = (nodes: IAccordionListNode[] = []): void => {
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          allSectionIds.push(node.id);
          getIdsFromNodes(node.children);
        } else {
          allStoryIds.push(node.id);
          if (toggledStoriesVisibilityMap[node.id] === true && node.id !== TOC_NODE_ID && node.id !== coverStoryId) {
            visibleStories.push(node.id);
          }
        }
      });
    };

    getIdsFromNodes(updatedStoriesList);
    return { allSectionIds, allStoryIds, visibleStories };
  }, [updatedStoriesList, toggledStoriesVisibilityMap, coverStoryId]);
  useEffect(() => {
    if (!updatedStoriesList) return;

    // Default new sections to expanded but preserve the user's collapse state.
    // The functional updater is critical: a plain `setToggledSectionsMap(allExpanded)`
    // would clobber the user's choice every time this effect re-fires (which can
    // happen on any parent render because `onToggledStoriesVisibilityMapChange`
    // is not memoised in the parent).
    setToggledSectionsMap((prev) => {
      const unseenSections = allSectionIds.filter((id) => !(id in prev));
      if (unseenSections.length === 0) return prev;
      const updated = { ...prev };
      unseenSections.forEach((id) => { updated[id] = true; });
      return updated;
    });

    if (Object.keys(toggledStoriesVisibilityMap).length > 0) {
      // Map already exists. Stories added after the initial load (e.g. added
      // in another tab while this page is open) won't be in the map yet.
      // Default any absent story to visible.
      const unseenIds = allStoryIds.filter(id => !(id in toggledStoriesVisibilityMap));
      if (unseenIds.length > 0) {
        const updated = { ...toggledStoriesVisibilityMap };
        unseenIds.forEach(id => { updated[id] = true; });
        onToggledStoriesVisibilityMapChange(updated);
      }
    }
  }, [updatedStoriesList, allSectionIds, allStoryIds, toggledStoriesVisibilityMap, onToggledStoriesVisibilityMapChange]);

  const toggleDrawer = () => setOpenDrawer(!openDrawer);

  const onNodeToggle = (node: IAccordionListNode) => {
    if (node.id) {
      setToggledSectionsMap((prev) => ({
        ...prev,
        [node.id]: !prev[node.id]
      }));
    }
  };

  const onStoryVisibilityToggle = (node: IAccordionListNode) => {

    if (node.id) {
      const updatedToggledStoriesVisibilityMap = {
        ...toggledStoriesVisibilityMap,
        [node.id]: !toggledStoriesVisibilityMap[node.id]
      }
      if (Object.values(updatedToggledStoriesVisibilityMap).every(value => value === false)) {
        return;
      }
      onToggledStoriesVisibilityMapChange({
        ...updatedToggledStoriesVisibilityMap
      });
      const newVisibleStories = visibleStories.filter((story) => updatedToggledStoriesVisibilityMap[story] === true);
      if (node.id === selectedStoryId) {
        toggledStoriesVisibilityMap[TOC_NODE_ID] ? onStoryChange(TOC_NODE_ID) : coverStoryId ? onStoryChange(coverStoryId) : onStoryChange(newVisibleStories[0]);
      }
    }
  };
  const DrawerHeader = (
    <Box sx={styles.drawerHeader}>
      <Typography variant="h3">Index</Typography>
      <IconButton size="large" onClick={toggleDrawer} aria-label={messages.closeSidebar}>
        <ArrowExpandLeft />
      </IconButton>
    </Box>
  );

  return (
    <>
      {!openDrawer && (
        <Tooltip title={messages.index} placement="top">
          <IconButton onClick={toggleDrawer} size="large" aria-label={messages.openSidebar} sx={styles.menuIcon}>
            <MenuIcon />
          </IconButton>
        </Tooltip>
      )}

      <Box sx={styles.drawerContainer}>
        <Drawer
          data-test="storiesListSidebar"
          variant="persistent"
          anchor="left"
          open={openDrawer}
          sx={openDrawer ? styles.drawer : styles.drawerClosed}
        >
          {DrawerHeader}
          <Box sx={styles.storiesList}>
            {updatedStoriesList && updatedStoriesList.length > 0 && (
              <>
                {renderAccordionNodes({
                  nodes: updatedStoriesList,
                  level: 0,
                  toggledSectionsMap,
                  toggledStoriesVisibilityMap,
                  messages,
                  onNodeToggle,
                  onStoryVisibilityToggle,
                  selectedStoryId,
                  onStoryChange,
                  coverStoryId,
                  storyMode,
                  visibleStories: visibleStories || []
                })}
              </>
            )}
          </Box>
        </Drawer>
        {openDrawer && <Divider orientation="vertical" flexItem />}
      </Box>
    </>
  );
};

export const StoriesListSidebar = React.memo(StoriesListSidebarComponent);
StoriesListSidebar.displayName = 'StoriesListSidebar';
